import { type ReactNode } from 'react';
import { Dices, Lock, LockOpen } from 'lucide-react';
import clsx from 'clsx';
import type { ParamBulkSpec } from '../../core/params/bulk';
import { effectiveRange, isNarrowed } from '../../core/params/bulk';
import type { Curve } from '../../core/params/curve';
import type { Param, ParamValue } from '../../core/params/types';
import type { Vec2 } from '../../core/math';
import { Slider } from '../controls/Slider';
import { RangeSlider } from '../controls/RangeSlider';
import { XYPad } from '../controls/XYPad';
import { CurveEditor } from '../controls/CurveEditor';
import { ColorInput, Segmented, Select, Toggle } from '../controls/Basics';
import { PaletteEditor } from '../controls/PaletteEditor';
import type { PaletteSpec } from '../../core/palette';
import { BulkRangePopover } from './BulkRangePopover';

export interface ParamControlProps {
  param: Param;
  value: ParamValue;
  bulk: ParamBulkSpec | undefined;
  onChange: (value: ParamValue) => void;
  onBulkChange: (spec: ParamBulkSpec | undefined) => void;
  onToggleLock: () => void;
  onRandomize: () => void;
}

/**
 * Renders one parameter from its schema description.
 *
 * This is the only place that knows how a parameter *looks*. Techniques never write
 * controls; they describe parameters, and every one of them arrives here with the same
 * lock, randomise and exploration-range affordances attached.
 */
export function ParamControl(props: ParamControlProps): JSX.Element | null {
  const { param, value, bulk, onChange, onBulkChange, onToggleLock, onRandomize } = props;
  const locked = bulk?.locked === true;

  const bulkButton = (
    <BulkRangePopover
      param={param}
      spec={bulk}
      currentValue={typeof value === 'number' || typeof value === 'string' ? value : undefined}
      onChange={onBulkChange}
    />
  );

  switch (param.kind) {
    case 'float':
    case 'int':
      return (
        <Slider
          label={param.label}
          value={typeof value === 'number' ? value : param.default}
          onChange={(v) => onChange(param.kind === 'int' ? Math.round(v) : v)}
          min={param.min}
          max={param.max}
          step={param.step}
          defaultValue={param.default}
          unit={param.unit}
          scale={param.scale}
          tooltip={param.tooltip}
          precision={param.precision}
          locked={locked}
          onToggleLock={param.neverRandomise ? undefined : onToggleLock}
          onRandomize={param.neverRandomise ? undefined : onRandomize}
          headerExtra={bulkButton}
          bulkRange={isNarrowed(param, bulk) ? effectiveRange(param, bulk) : undefined}
        />
      );

    case 'seed':
      return (
        <Header {...props} locked={locked} bulkButton={null}>
          <div className="flex items-center gap-1.5">
            <input
              value={typeof value === 'number' ? value : param.default}
              onChange={(e) => {
                const v = Number.parseInt(e.target.value.replace(/\D/g, ''), 10);
                onChange(Number.isFinite(v) ? v : 0);
              }}
              className="field-input font-mono text-[11px]"
              spellCheck={false}
            />
            <button type="button" className="ghost-button h-7 w-7 shrink-0 p-0" title="Neu würfeln" onClick={onRandomize}>
              <Dices size={12} />
            </button>
          </div>
        </Header>
      );

    case 'bool':
      return (
        <div className="group/param flex items-center gap-1">
          <div className="min-w-0 flex-1">
            <Toggle
              checked={typeof value === 'boolean' ? value : param.default}
              onChange={onChange}
              label={param.label}
              tooltip={param.tooltip}
            />
          </div>
          <Icons locked={locked} onToggleLock={onToggleLock} onRandomize={onRandomize} param={param} />
        </div>
      );

    case 'enum': {
      const current = typeof value === 'string' ? value : param.default;
      return (
        <Header {...props} locked={locked} bulkButton={bulkButton}>
          {param.segmented ? (
            <Segmented
              size="sm"
              value={current}
              onChange={onChange}
              options={param.options.map((o) => ({ value: o.value, label: o.label, title: o.label }))}
            />
          ) : (
            <Select value={current} onChange={onChange} options={param.options.map((o) => ({ value: o.value, label: o.label }))} />
          )}
        </Header>
      );
    }

    case 'color':
      return (
        <Header {...props} locked={locked} bulkButton={null}>
          <ColorInput value={typeof value === 'string' ? value : param.default} onChange={onChange} />
        </Header>
      );

    case 'palette':
      return (
        <Header {...props} locked={locked} bulkButton={null} stacked>
          <PaletteEditor
            value={(value && typeof value === 'object' && 'source' in value ? value : param.default) as PaletteSpec}
            onChange={onChange}
          />
        </Header>
      );

    case 'text':
      return (
        <Header {...props} locked={locked} bulkButton={bulkButton} stacked={param.multiline}>
          {param.multiline ? (
            <textarea
              rows={3}
              value={typeof value === 'string' ? value : param.default}
              onChange={(e) => onChange(e.target.value)}
              placeholder={param.placeholder}
              spellCheck={false}
              className="field-input resize-y font-mono text-[13px] leading-snug"
            />
          ) : (
            <input
              value={typeof value === 'string' ? value : param.default}
              onChange={(e) => onChange(e.target.value)}
              placeholder={param.placeholder}
              spellCheck={false}
              className="field-input text-[11px]"
            />
          )}
        </Header>
      );

    case 'range': {
      const current = (Array.isArray(value) ? value : param.default) as [number, number];
      return (
        <Header {...props} locked={locked} bulkButton={null} stacked>
          <RangeSlider
            value={current}
            onChange={onChange}
            min={param.min}
            max={param.max}
            step={param.step}
            scale={param.scale}
          />
          <div className="flex justify-between font-mono text-[10px] text-ink-500">
            <span>{current[0].toFixed(param.precision ?? 2)}</span>
            <span className="text-ink-600">{param.unit}</span>
            <span>{current[1].toFixed(param.precision ?? 2)}</span>
          </div>
        </Header>
      );
    }

    case 'vec2':
      return (
        <Header {...props} locked={locked} bulkButton={null} stacked>
          <XYPad
            value={(value as Vec2) ?? param.default}
            onChange={onChange}
            min={param.min}
            max={param.max}
            step={param.step}
            defaultValue={param.default}
            square={param.square}
            unit={param.unit}
          />
        </Header>
      );

    case 'curve':
      return (
        <Header {...props} locked={locked} bulkButton={null} stacked>
          <CurveEditor
            value={(Array.isArray(value) ? value : param.default) as Curve}
            onChange={onChange}
            xLabel={param.xLabel}
            yLabel={param.yLabel}
          />
        </Header>
      );
  }
}

// ── Shared chrome ────────────────────────────────────────────────────────────

function Icons({
  param,
  locked,
  onToggleLock,
  onRandomize,
}: {
  param: Param;
  locked: boolean;
  onToggleLock: () => void;
  onRandomize: () => void;
}): JSX.Element | null {
  if (param.neverRandomise) return null;
  return (
    <>
      <button
        type="button"
        title="Nur diesen Wert neu würfeln"
        onClick={onRandomize}
        className="ghost-button h-5 w-5 p-0 opacity-0 transition-opacity group-hover/param:opacity-100"
      >
        <Dices size={11} />
      </button>
      <button
        type="button"
        title={locked ? 'Entsperren — Randomize und Batch dürfen wieder ändern' : 'Sperren — vor Randomize und Batch schützen'}
        onClick={onToggleLock}
        className={clsx(
          'ghost-button h-5 w-5 p-0 transition-opacity',
          locked ? 'text-accent-500 opacity-100' : 'opacity-0 group-hover/param:opacity-100',
        )}
      >
        {locked ? <Lock size={11} /> : <LockOpen size={11} />}
      </button>
    </>
  );
}

function Header({
  param,
  locked,
  bulkButton,
  stacked = false,
  onToggleLock,
  onRandomize,
  children,
}: ParamControlProps & { locked: boolean; bulkButton: ReactNode; stacked?: boolean; children: ReactNode }): JSX.Element {
  return (
    <div className={clsx('group/param py-1', stacked ? 'space-y-1' : 'flex items-center gap-2')}>
      <div className={clsx('flex items-center gap-1', stacked ? '' : 'w-[100px] shrink-0')}>
        <span className="min-w-0 flex-1 truncate text-[11px] text-ink-300" title={param.tooltip ?? param.label}>
          {param.label}
        </span>
        {bulkButton}
        <Icons param={param} locked={locked} onToggleLock={onToggleLock} onRandomize={onRandomize} />
      </div>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}
